import { observer } from "mobx-react-lite";
import { useState } from "react";
import {
  CheckCircle2,
  Clock,
  FileText,
  Search,
  UserCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Progress from "@/components/common/Progress";
import {
  useESGCollaborationStore,
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGReportWorkflowStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { buildCategoryStats } from "@/features/esg/utils/review-utils";

const CATEGORY_STYLES = {
  Environment: {
    dot: "bg-blue-500",
    text: "text-blue-600",
    bg: "bg-blue-500/10",
  },
  Social: {
    dot: "bg-purple-500",
    text: "text-purple-600",
    bg: "bg-purple-500/10",
  },
  Governance: {
    dot: "bg-amber-500",
    text: "text-amber-600",
    bg: "bg-amber-500/10",
  },
};

const ReviewDisclosureTable = observer(() => {
  const { t } = useTran();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const {
    filteredSelectedCodesByCategory,
    availableDisclosures,
    customDisclosures,
  } = useESGDisclosureSelectionStore();
  const {
    completedCountByCategory,
    isDisclosureCompleted,
    entriesByCode,
    setCurrentDisclosure,
    setActiveCategory,
  } = useESGDataEntryStore();
  const { delegatedDisclosures } = useESGCollaborationStore();
  const { goToStep } = useESGReportWorkflowStore();

  const categoryStats = buildCategoryStats(
    filteredSelectedCodesByCategory,
    completedCountByCategory,
  );

  const disclosureMap = new Map(
    [...availableDisclosures, ...customDisclosures].map((d) => [d.code, d]),
  );

  // Build flat disclosure list with metadata
  const allDisclosures = [];
  Object.entries(filteredSelectedCodesByCategory).forEach(
    ([category, codes]) => {
      codes.forEach((code) => {
        const disclosure = disclosureMap.get(code);
        const entries = entriesByCode.get(code) || [];
        const proofCount = entries.reduce(
          (sum, e) => sum + (e.proofs?.length || 0),
          0,
        );
        const completed = isDisclosureCompleted(code);
        const delegated = delegatedDisclosures.has(code);
        const delegation = delegatedDisclosures.get(code);

        allDisclosures.push({
          code,
          title: disclosure?.title || disclosure?.name || "",
          category,
          entryCount: entries.length,
          proofCount,
          completed,
          delegated,
          delegationEmail: delegation?.email,
        });
      });
    },
  );

  // Apply filters
  const filtered = allDisclosures.filter((d) => {
    if (statusFilter === "Pending" && d.completed) return false;
    if (categoryFilter !== "All" && d.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !d.code.toLowerCase().includes(q) &&
        !d.title.toLowerCase().includes(q) &&
        !d.delegationEmail?.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const handleOpen = (d) => {
    setActiveCategory(d.category);
    setCurrentDisclosure(d.code);
    goToStep(3);
  };

  return (
    <div className="h-full rounded-radius border border-border bg-card shadow-sm">
      {/* Header + status toggle */}
      <div className="grid grid-cols-[1fr_auto] items-start border-b border-border p-4">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {t("Report content")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("Review disclosures before publishing or exporting.")}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant={statusFilter === "All" ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => setStatusFilter("All")}
          >
            {t("All")}
          </Button>
          <Button
            size="sm"
            variant={statusFilter === "Pending" ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => setStatusFilter("Pending")}
          >
            {t("Pending")}
          </Button>
        </div>
      </div>

      <div className="p-4">
        {/* Search + category filter */}
        <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("Search disclosures, IDs, delegatee...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">{t("All")}</SelectItem>
              <SelectItem value="Environment">{t("Environment")}</SelectItem>
              <SelectItem value="Social">{t("Social")}</SelectItem>
              <SelectItem value="Governance">{t("Governance")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category summary cards */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          {categoryStats.map((stat) => {
            const styles = CATEGORY_STYLES[stat.category];
            return (
              <div
                key={stat.category}
                className={`cursor-pointer rounded-radius border p-3 transition-colors hover:border-border ${
                  categoryFilter === stat.category
                    ? "border-primary/50 bg-primary/5"
                    : "border-border"
                }`}
                onClick={() =>
                  setCategoryFilter((prev) =>
                    prev === stat.category ? "All" : stat.category,
                  )
                }
              >
                <div className="grid grid-cols-[1fr_auto] items-center">
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${styles.dot}`}
                    />
                    <p className="text-xs font-semibold text-foreground">
                      {t(stat.category)}
                    </p>
                  </div>
                  <Badge
                    className={`${styles.bg} ${styles.text} border-0 text-[11px]`}
                  >
                    {stat.percentage}%
                  </Badge>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {stat.completed}/{stat.total} {t("complete")}
                </p>
                <div className="mt-2">
                  <Progress
                    value={stat.percentage}
                    indicatorClassName={styles.dot}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Table headers */}
        <div className="mt-4 grid grid-cols-12 gap-2 border-b border-border pb-2">
          <p className="col-span-5 text-[11px] font-medium text-muted-foreground">
            {t("Disclosure")}
          </p>
          <p className="col-span-2 text-[11px] font-medium text-muted-foreground">
            {t("Pillar")}
          </p>
          <p className="col-span-2 text-[11px] font-medium text-muted-foreground">
            {t("Evidence")}
          </p>
          <p className="col-span-2 text-[11px] font-medium text-muted-foreground">
            {t("Status")}
          </p>
          <p className="col-span-1 text-[11px] font-medium text-muted-foreground">
            {t("Action")}
          </p>
        </div>

        {/* Table rows */}
        <div className="mt-2 space-y-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              {t("No disclosures match the filters.")}
            </p>
          ) : (
            filtered.map((d) => {
              const styles = CATEGORY_STYLES[d.category];
              return (
                <div
                  key={d.code}
                  className="grid grid-cols-12 items-center gap-2 rounded-radius border border-border/60 bg-background/60 px-3 py-2"
                >
                  {/* Disclosure */}
                  <div className="col-span-5 flex items-center gap-2">
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-radius ${styles.bg}`}
                    >
                      <FileText className={`h-3.5 w-3.5 ${styles.text}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-foreground">
                        {d.code}
                      </p>
                      {d.delegationEmail && (
                        <div className="flex items-center gap-1">
                          <UserCircle className="h-2.5 w-2.5 text-amber-500" />
                          <p className="text-[11px] text-amber-600 truncate">
                            {d.delegationEmail}
                          </p>
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground truncate">
                        {d.title}
                      </p>
                    </div>
                  </div>

                  {/* Pillar */}
                  <div className="col-span-2 flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${styles.dot}`} />
                    <p className="text-xs text-foreground">{t(d.category)}</p>
                  </div>

                  {/* Evidence */}
                  <div className="col-span-2">
                    <p className="text-xs text-foreground">
                      <span className="font-medium">{d.entryCount}</span>{" "}
                      {t("entry")} ·{" "}
                      <span className="font-medium">{d.proofCount}</span>{" "}
                      {t("proof")}
                    </p>
                    {d.proofCount === 0 && (
                      <p className="text-[11px] text-muted-foreground">
                        {t("No proofs attached")}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    {d.completed ? (
                      <Badge className="border-0 bg-emerald-500/10 text-emerald-600 text-[11px]">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t("Completed")}
                      </Badge>
                    ) : d.delegated ? (
                      <Badge className="border-0 bg-amber-500/10 text-amber-600 text-[11px]">
                        <UserCircle className="mr-1 h-3 w-3" />
                        {t("Delegated")}
                      </Badge>
                    ) : (
                      <Badge className="border-0 bg-muted text-muted-foreground text-[11px]">
                        <Clock className="mr-1 h-3 w-3" />
                        {t("Pending")}
                      </Badge>
                    )}
                  </div>

                  {/* Action */}
                  <div className="col-span-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[11px]"
                      onClick={() => handleOpen(d)}
                    >
                      {t("Open")}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Tip */}
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">{t("Tip")}:</span>{" "}
            {t("Attach proofs to strengthen your report and speed up reviews.")}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ReviewDisclosureTable;
